import { type Api } from "api-client";
import { useTranslation } from "react-i18next";

import { NotificationAthleteInfo } from "src/components/NotificationAthleteInfo";

interface NotificationMessageProps {
  notification: Api.GetAllNotifications.Output.Notification;
}

export function NotificationMessage(props: NotificationMessageProps) {
  const { t } = useTranslation();
  const notification = props.notification;

  switch (notification.type) {
    case "ORGANIZATION_MEMBER_INVITED":
      return t("notification.organizationMemberInvited", {
        organizationName: notification.organization?.name,
      });
    case "JOIN_REQUEST_WORKFLOW":
      return t("notification.joinRequestWorkflow", {
        author: notification.sender?.name,
        workflow: notification.workflow?.name,
      });
    case "ACCEPTED_JOIN_REQUEST_WORKFLOW":
      return t("notification.acceptedJoinRequestWorkflow", {
        workflow: notification.workflow?.name,
      });
    case "REJECTED_JOIN_REQUEST_WORKFLOW":
      return t("notification.rejectedJoinRequestWorkflow", {
        workflow: notification.workflow?.name,
      });
    case "PROFILE_CONTACT_SUBMITTED":
      return t("notification.profileContactSubmitted", {
        author: notification.profileLead?.name,
      });
    case "PRODUCT_PURCHASED":
      return t("notification.productPurchased", {
        athlete: notification.athlete.name,
        product: notification.product.name,
      });
    case "PRODUCT_PAYMENT_FAILED":
      return t("notification.productPaymentFailed", {
        athlete: notification.athlete.name,
        product: notification.product.name,
      });
    case "PRODUCT_SUBSCRIPTION_CANCEL_SCHEDULED":
      return t("notification.productSubscriptionCancelScheduled", {
        athlete: notification.athlete.name,
        product: notification.product.name,
      });
    case "PRODUCT_SUBSCRIPTION_ENDED":
      return t("notification.productSubscriptionEnded", {
        athlete: notification.athlete.name,
        product: notification.product.name,
      });
    case "PRODUCT_PAYMENT_DISPUTED":
      return t("notification.productPaymentDisputed", {
        athlete: notification.athlete.name,
        product: notification.product.name,
      });
    case "PRODUCT_PAYMENT_RECEIVED":
      return t("notification.productPaymentReceived", {
        athlete: notification.athlete.name,
        product: notification.product.name,
      });
    case "PRODUCT_SUBSCRIPTION_CANCEL_UNDONE":
      return t("notification.productSubscriptionCancelUndone", {
        athlete: notification.athlete.name,
        product: notification.product.name,
      });
    case "PRODUCT_CHECKOUT_ABANDONED":
      return (
        <>
          {t("notification.productCheckoutAbandoned", {
            athlete: notification.productCheckout.athleteName,
            product: notification.productCheckout.productName,
          })}
          <NotificationAthleteInfo
            description={t("notification.productCheckoutAbandonedInfo", {
              athlete: notification.productCheckout.athleteName,
              product: notification.productCheckout.productName,
            })}
            athleteEmail={notification.productCheckout.athleteEmail}
          />
        </>
      );
    case "PRODUCT_ATHLETE_REGISTERED":
      return (
        <>
          {t("notification.productAthleteRegistered", {
            athlete: notification.productCheckout.athleteName,
            product: notification.productCheckout.productName,
          })}
          <NotificationAthleteInfo
            description={t("notification.productAthleteRegisteredInfo", {
              athlete: notification.productCheckout.athleteName,
              product: notification.productCheckout.productName,
            })}
            athleteEmail={notification.productCheckout.athleteEmail}
          />
        </>
      );
    case "COMMENT_ADDED":
      return (
        <>
          {`${t("commentAdded", {
            author: notification.sender?.name,
          })} `}
          <span className="font-bold">
            {notification.comment?.exerciseName ??
              notification.comment?.workoutName ??
              "-"}
          </span>
          {notification.comment?.exerciseName &&
            notification.comment.workoutName && (
              <>
                {` ${t("commentAddedIn")} `}
                <span className="font-bold">
                  {notification.comment.workoutName}
                </span>
              </>
            )}
        </>
      );
  }
}
